import React, { useMemo } from 'react'
import { Container } from 'react-bootstrap'

import { getIntegrationUserTrackingDetails } from '../../../apps/professional-app/fa/tracking'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import {
  CONTACT_UPDATE_STEP,
  selectContact,
  selectContactUpdateStep,
  setContactUpdateStep,
  setCreateNewCRMContact,
  setExternalId,
} from '../../../store/meetingSummary/contactUpdate'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE, IntegrationServiceId } from '../../../common/integrations'
import { User } from '../../../models/User'

import { SegmentedExternalContactUpdateView } from '../../UpdateExternalContact/SegmentedExternalContactUpdateView'
import { ExternalEmailContactUpdateProps, useInitializeContactChange } from '../../UpdateExternalContact/hooks'
import {
  DefaultExternalContactUpdateViewV2,
  UpdateExternalContactModal,
  UpdateExternalContactModalFooter,
} from '../../UpdateExternalContact/UpdateExternalContactModal'
import { UpdateExternalContactModalInitialization } from '../../UpdateExternalContact/UpdateExternalContactModalInitialization'
import { UpdateExternalContactSelectStep } from '../../UpdateExternalContact/UpdateExternalContactSelectStep'

interface UpdateExternalEmailContactModalProps {
  user: User
  serviceId: string
  emailConnector: string
  threadId: string
  show: boolean
  onCancel: () => void
  onSave: () => void
}

export const UpdateExternalEmailContactModal: React.FC<UpdateExternalEmailContactModalProps> = ({
  serviceId,
  emailConnector,
  threadId,
  user,
  show,
  onCancel,
  onSave,
}) => {
  const contactUpdateStep = useAppSelector(selectContactUpdateStep)
  const dispatch = useAppDispatch()
  const integration = user.integrations?.find((e) => e.serviceId === serviceId)
  if (integration) {
    dispatch(setContactUpdateStep(CONTACT_UPDATE_STEP.CONTACT_UPDATE_STEP))
    dispatch(setExternalId(integration.externalId))
    dispatch(setCreateNewCRMContact(false))
  }

  const BodyComponent = (
    <UpdateExternalEmailContactModalBody serviceId={serviceId} user={user} emailConnector={emailConnector} threadId={threadId} onCancel={onCancel} />
  )

  const FooterComponent = (
    <UpdateExternalEmailContactModalFooter
      serviceId={serviceId}
      user={user}
      emailConnector={emailConnector}
      threadId={threadId}
      onCancel={onCancel}
      onSave={onSave}
    />
  )

  return (
    <UpdateExternalContactModal
      user={user}
      serviceId={serviceId}
      show={show}
      onCancel={onCancel}
      BodyComponent={BodyComponent}
      FooterComponent={contactUpdateStep === CONTACT_UPDATE_STEP.CONTACT_UPDATE_STEP ? FooterComponent : null}
    />
  )
}

interface UpdateExternalEmailContactModalBodyProps {
  serviceId: string
  user: User
  emailConnector: string
  threadId: string
  onCancel: () => void
}

const UpdateExternalEmailContactModalBody: React.FC<UpdateExternalEmailContactModalBodyProps> = ({ serviceId, user, emailConnector, threadId, onCancel }) => {
  return (
    <>
      <UpdateExternalContactModalInitialization serviceId={serviceId} />
      <UpdateExternalEmailContactContent
        serviceId={serviceId}
        contactId={user.id}
        user={user}
        emailConnector={emailConnector}
        threadId={threadId}
        onCancel={onCancel}
      />
    </>
  )
}

interface UpdateExternalEmailContactContentProps {
  serviceId: string
  contactId: string
  user: User
  emailConnector: string
  threadId: string
  onCancel: () => void
}

export const UpdateExternalEmailContactContent: React.FC<UpdateExternalEmailContactContentProps> = ({
  serviceId,
  contactId,
  user,
  emailConnector,
  threadId,
  onCancel,
}) => {
  const { trackingClient } = useTracking()
  const contactUpdateStep = useAppSelector(selectContactUpdateStep)

  const handleKeySelected = (selectedKey: string, isUnselect: boolean) => {
    trackingClient.track({
      eventName: TrackEvent.CRM_CONTACT_UPDATE_FIELD_SELECTED,
      eventDetails: {
        emailConnector,
        threadId,
        selectedKey,
      },
    })
    if (isUnselect) {
      trackingClient.track({
        eventName: TrackEvent.CONTACT_UPDATE_SECTIONS_UNCHECKED,
        eventDetails: {
          serviceId,
          emailConnector,
          threadId,
          selectedKey,
        },
      })
    }
  }

  const updateProps: ExternalEmailContactUpdateProps = useMemo(() => ({ type: 'email', emailConnector, threadId }), [emailConnector, threadId])

  useInitializeContactChange({
    serviceId,
    user,
    updateProps,
    onCancel,
  })

  const ExternalContactUpdateView = INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE.includes(serviceId as IntegrationServiceId)
    ? SegmentedExternalContactUpdateView
    : DefaultExternalContactUpdateViewV2

  return (
    <Container className='d-flex flex-column zocks-gap-1.25 p-0'>
      <div>
        {contactUpdateStep === CONTACT_UPDATE_STEP.CONTACT_SELECT_STEP ? (
          <>
            <UpdateExternalContactSelectStep serviceId={serviceId} onCancel={onCancel} user={user} />
          </>
        ) : null}
        {contactUpdateStep === CONTACT_UPDATE_STEP.CONTACT_UPDATE_STEP ? (
          <ExternalContactUpdateView serviceId={serviceId} contactId={contactId} onKeySelected={handleKeySelected} />
        ) : null}
      </div>
    </Container>
  )
}

interface UpdateExternalEmailContactModalFooterProps {
  serviceId: string
  user: User
  emailConnector: string
  threadId: string
  onCancel: () => void
  onSave: () => void
}

const UpdateExternalEmailContactModalFooter: React.FC<UpdateExternalEmailContactModalFooterProps> = ({
  serviceId,
  user,
  emailConnector,
  threadId,
  onCancel,
  onSave,
}) => {
  const updateProps: ExternalEmailContactUpdateProps = useMemo(() => ({ type: 'email', emailConnector, threadId }), [emailConnector, threadId])
  const contact = useAppSelector(selectContact)

  const { trackingClient } = useTracking()

  const handleRerun = () => {
    trackingClient.track({
      eventName: TrackEvent.CRM_CONTACT_UPDATE_RERUN,
      eventDetails: {
        emailConnector,
        threadId,
        ...getIntegrationUserTrackingDetails(contact),
        userId: user.id,
      },
    })
  }

  const handleSave = () => {
    trackingClient.track({
      eventName: TrackEvent.CRM_CONTACT_UPDATE_SAVED,
      eventDetails: {
        emailConnector,
        threadId,
        ...getIntegrationUserTrackingDetails(contact),
        userId: user.id,
      },
    })
    onSave()
  }

  return (
    <UpdateExternalContactModalFooter
      serviceId={serviceId}
      user={user}
      updateProps={updateProps}
      onRerun={handleRerun}
      onSave={handleSave}
      onCancel={onCancel}
    />
  )
}
